import { Link } from "react-router-dom";
import { FileText, ArrowUpRight, Download } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const RESOURCES = [
  { title: "Executive Overview", text: "A two-page summary of the problem, the framework, and the pilot, written for agency and hospital leadership.", file: "/docs/temrp-executive-overview.pdf" },
  { title: "Institutional White Paper", text: "The full concept and implementation paper: evidence base, framework, governance, and evaluation design.", file: "/docs/temrp-institutional-white-paper.pdf" },
  { title: "One-Page Brief", text: "A single-page summary suitable for distribution ahead of an initial review.", file: "/docs/temrp-one-page-brief.pdf" },
  { title: "Pilot Model Summary", text: "Timeline, stage gates, participant profile, and proposed measures in a standalone document.", file: "/docs/temrp-pilot-model-summary.pdf" },
  { title: "Framework Diagram", text: "The five-component architecture and closed-loop diagram in a print-ready format.", file: "/docs/temrp-framework-diagram.pdf" },
  { title: "Partnership Guide", text: "Engagement models, contribution options by institution type, and next-step guidance.", file: "/docs/temrp-partnership-guide.pdf" },
];

export default function Resources() {
  return (
    <main data-testid="resources-page">
      <PageHead
        chapter="09"
        eyebrow="Resources"
        titleLines={["Resource", "Library."]}
        lede="Executive-level and technical materials for reviewers, partners, and evaluators. All documents are available for direct download."
        image={IMAGES.gearDetail}
      />

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
          {RESOURCES.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.06} className="bg-ink">
              <div data-testid={`resource-card-${i}`} className="group flex flex-col p-8 h-full hover:bg-surface transition-colors duration-300">
                <FileText size={22} className="text-signal" />
                <h3 className="mt-5 font-display font-bold text-xl text-white tracking-tight">{r.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{r.text}</p>
                <a
                  href={r.file}
                  download
                  data-testid={`resource-download-${i}`}
                  className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white border border-line hover:border-signal hover:text-signal px-4 py-3 w-fit transition-colors"
                >
                  <Download size={13} /> Download PDF
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 border-l-2 border-signal bg-surface p-8 max-w-3xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">Requesting a document</p>
            <p className="text-sm text-slate-300 leading-relaxed">
              If a resource you need is not yet available, request it directly and it will be shared as soon as it is ready for release.
            </p>
            <Link to="/contact" data-testid="resources-cta" className="mt-6 inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              Request a Document <ArrowUpRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
